import React, { useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import {
  FaCheckCircle,
  FaEnvelope,
  FaVideo,
  FaClipboardList,
  FaArrowLeft,
} from "react-icons/fa";
import { initPixels, track } from "../lib/tracking";

/* ─── animations ─── */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0%   { opacity: 0; transform: scale(0.6); }
  60%  { transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
`;

/* ─── layout ─── */

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #07090f 0%, #0a0c1e 60%, #07090f 100%);
  color: #fff;
  font-family: "Roboto", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;

  @media (max-width: 768px) {
    padding: 40px 20px;
    align-items: flex-start;
  }
`;

const Inner = styled.div`
  max-width: 640px;
  width: 100%;
  text-align: center;
  animation: ${fadeInUp} 0.7s ease-out both;
`;

const CheckMark = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.35);
  margin-bottom: 28px;
  animation: ${popIn} 0.6s ease-out 0.15s both;

  svg {
    font-size: 40px;
    color: #10b981;
  }

  @media (max-width: 768px) {
    width: 68px;
    height: 68px;
    margin-bottom: 22px;
    svg { font-size: 32px; }
  }
`;

const Title = styled.h1`
  font-size: 2.4em;
  font-weight: 800;
  line-height: 1.18;
  letter-spacing: -0.5px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #00c8ff 55%, #005bea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.7em;
  }
`;

const Subtitle = styled.p`
  font-size: 1.08em;
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.7;
  margin: 0 auto 40px;
  max-width: 480px;

  @media (max-width: 768px) {
    font-size: 0.98em;
    margin-bottom: 32px;
  }
`;

const StepsCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  padding: 28px 30px;
  text-align: left;
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.7s ease-out 0.3s both;

  @media (max-width: 768px) {
    padding: 22px 20px;
  }
`;

const StepsTitle = styled.h2`
  font-size: 0.85em;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 20px;
`;

const StepsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Step = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 14px;

  svg {
    color: #00c8ff;
    font-size: 16px;
    margin-top: 3px;
    flex-shrink: 0;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  strong {
    font-size: 0.97em;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  span {
    font-size: 0.9em;
    color: rgba(255, 255, 255, 0.5);
    line-height: 1.5;
  }
`;

const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: rgba(255, 255, 255, 0.45);
  text-decoration: none;
  font-size: 0.92em;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.05);
  }
`;

/* ─── metadatos ─── */

const PAGE_TITLE = "Reunión agendada | Monaco IT Services";
const PAGE_DESCRIPTION =
  "Tu reunión con Monaco IT Services quedó programada correctamente. Te enviamos la confirmación por correo.";

function useThankYouMeta() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const previousDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", PAGE_DESCRIPTION);
    return () => {
      document.title = previousTitle;
      if (desc && previousDesc !== null) desc.setAttribute("content", previousDesc);
    };
  }, []);
}

/*
  La reserva que acaba de ocurrir la deja /call en sessionStorage antes de
  redirigir. La usamos para dos cosas: derivar un event_id estable (así un
  refresh no cuenta otra conversión) y pasarle al servidor el URI del invitee,
  con el que consulta a Calendly y manda email, nombre y teléfono hasheados.
  Eso es justamente lo que sube el match quality, que hoy está en 4.44 sobre 10.
*/
const BOOKING_KEY = "monaco_last_booking";

function readLastBooking() {
  try {
    const raw = sessionStorage.getItem(BOOKING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Solo confiamos en una reserva reciente (30 min), por si quedó pegada.
    if (!parsed || Date.now() - (parsed.ts || 0) > 30 * 60 * 1000) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

/* ─── component ─── */

export const ThankYouPage = () => {
  useThankYouMeta();

  const booking = useMemo(() => readLastBooking(), []);

  useEffect(() => {
    initPixels();

    const inviteeUri = booking?.inviteeUri;
    const inviteeUuid = inviteeUri ? String(inviteeUri).split("/").pop() : null;

    track(
      "CompleteRegistration",
      { content_name: "reunion_agendada" },
      {
        // Determinista: refrescar la página no genera una segunda conversión.
        eventId: inviteeUuid ? `cal_reg_${inviteeUuid}` : undefined,
        calendlyInviteeUri: inviteeUri || undefined,
      }
    );

    // Ya la usamos, la sacamos para que no se reutilice en otra visita.
    try {
      sessionStorage.removeItem(BOOKING_KEY);
    } catch (e) {
      /* swallow */
    }
  }, [booking]);

  return (
    <PageWrapper>
      <Inner>
        <CheckMark>
          <FaCheckCircle />
        </CheckMark>

        <Title>Tu reunión quedó agendada</Title>
        <Subtitle>
          Tu reunión con Monaco IT Services quedó programada correctamente.
          Te enviamos la confirmación por correo con el enlace para conectarte.
        </Subtitle>

        <StepsCard>
          <StepsTitle>Qué pasa ahora</StepsTitle>
          <StepsList>
            <Step>
              <FaEnvelope />
              <div>
                <strong>Revisa tu correo</strong>
                <span>
                  Llega la invitación con el enlace de Google Meet y la opción de
                  reagendar. Si no la ves, mira en spam o promociones.
                </span>
              </div>
            </Step>
            <Step>
              <FaClipboardList />
              <div>
                <strong>Llegamos con tu caso revisado</strong>
                <span>
                  Antes de la reunión miramos tu empresa y lo que nos contaste,
                  para no gastar los 30 minutos en presentaciones.
                </span>
              </div>
            </Step>
            <Step>
              <FaVideo />
              <div>
                <strong>Conéctate desde donde estés</strong>
                <span>
                  Es por videollamada y dura 30 minutos. Con el celular basta.
                </span>
              </div>
            </Step>
          </StepsList>
        </StepsCard>

        <BackLink href="/">
          <FaArrowLeft /> Volver al sitio
        </BackLink>
      </Inner>
    </PageWrapper>
  );
};
